import { Component, OnInit, Inject } from '@angular/core';
import { MatDialogModule, MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { UntypedFormGroup, UntypedFormBuilder, Validators, FormArray, AbstractControl, FormControl } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Artikel, StammdatenService, ArtikelStammdaten } from '@app/services/stammdaten.service';
import { AuthQuery } from '@app/auth/auth.query';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-dialog-artikel',
  templateUrl: './dialog-artikel.component.html',
  styleUrls: ['./dialog-artikel.component.css'],
})
export class ArtikelDialogComponent implements OnInit {
  artikel: Artikel;
  frmArtikel: UntypedFormGroup;
  
  // Dropdown data properties
  artikelArten: string[] = [];
  basiseinheiten: any[] = [];
  ausbringungen: any[] = [];
  statusOptions: any[] = [];
  produktarten: any[] = [];
  loading = true;
  isSubmitting = false;

  constructor(
    public dialogRef: MatDialogRef<ArtikelDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private _fb: UntypedFormBuilder,
    private stammdatenService: StammdatenService,
    private authQuery: AuthQuery,
    private snackBar: MatSnackBar
  ) {
    console.log('Dialog constructor called with data:', data);
    console.log('Dialog constructor data.artikel:', data?.artikel);
    this.artikel = data?.artikel || {
      id: 0,
      nummer: '',
      beschreibung: '',
      beschreibung_2: '',
      art: '',
      basiseinheit: '',
      code: '',
      beschreibung_code: '',
      in_code: '',
      alternative_beschreibung: '',
      belegtext: '',
      ausbringung: '',
      sortierung: 0,
      zulassungen: '',
      sicherheitsdatenblatt: '',
      systemkomponente: '',
      zulassungs_nummer: '',
      wirkstoff: '',
      status_id: 0,
      biozid_produktart: '',
      vk_exkl_ust: undefined,
      ek_preis: undefined,
      ep: undefined,
      kto_tab: '',
      grund_eh: '',
      indexerhoehung: 0,
      hauptlieferant: '',
      lagermenge: undefined,
      inaktiv: 0,
      art_typ: ''
    };

    this.frmArtikel = this._fb.group({
      id: [0],
      nummer: ['', Validators.required],
      beschreibung: ['', Validators.required],
      beschreibung_2: [''],
      art: [''],
      basiseinheit: [''],
      code: [''],
      beschreibung_code: [''],
      in_code: [''],
      alternative_beschreibung: [''],
      belegtext: [''],
      ausbringung: [''],
      sortierung: [0],
      zulassungen: [''],
      sicherheitsdatenblatt: [''],
      systemkomponente: [''],
      zulassungs_nummer: [''],
      wirkstoff: [''],
      status_id: [0],
      biozid_produktart: [''],
      vk_exkl_ust: [null],
      ek_preis: [null],
      ep: [null],
      kto_tab: [''],
      grund_eh: [''],
      indexerhoehung: [0],
      hauptlieferant: [''],
      lagermenge: [null],
      inaktiv: [0],
      art_typ: ['']
    });
  }

  get canSeeCostPrices(): boolean {
    const role = this.authQuery.role;
    return role === 1 || role === 2 || role === 11;
  }

  private getBmdFormValues(): Partial<Artikel> {
    return {
      vk_exkl_ust: this.artikel.vk_exkl_ust,
      ek_preis: this.artikel.ek_preis,
      ep: this.artikel.ep,
      kto_tab: this.artikel.kto_tab ?? '',
      grund_eh: this.artikel.grund_eh ?? '',
      indexerhoehung: this.artikel.indexerhoehung ?? 0,
      hauptlieferant: this.artikel.hauptlieferant ?? '',
      lagermenge: this.artikel.lagermenge,
      inaktiv: this.artikel.inaktiv ?? 0,
      art_typ: this.artikel.art_typ ?? ''
    };
  }

  private patchArtikelForm(): void {
    this.frmArtikel.patchValue({
      id: this.artikel.id,
      nummer: this.artikel.nummer,
      beschreibung: this.artikel.beschreibung,
      beschreibung_2: this.artikel.beschreibung_2,
      art: this.artikel.art,
      basiseinheit: this.artikel.basiseinheit,
      code: this.artikel.code,
      beschreibung_code: this.artikel.beschreibung_code,
      in_code: this.artikel.in_code,
      alternative_beschreibung: this.artikel.alternative_beschreibung,
      belegtext: this.artikel.belegtext,
      ausbringung: this.artikel.ausbringung,
      sortierung: this.artikel.sortierung,
      zulassungen: this.artikel.zulassungen,
      sicherheitsdatenblatt: this.artikel.sicherheitsdatenblatt,
      systemkomponente: this.artikel.systemkomponente,
      zulassungs_nummer: this.artikel.zulassungs_nummer,
      wirkstoff: this.artikel.wirkstoff,
      status_id: this.artikel.status_id,
      biozid_produktart: this.artikel.biozid_produktart,
      ...this.getBmdFormValues()
    });
  }

  ngOnInit() {
    console.log('Dialog ngOnInit called');
    // Load stammdaten for dropdowns first
    this.loadStammdaten();
  }

  /**
   * Load stammdaten for dropdown options
   */
  loadStammdaten() {
    console.log('Loading artikel stammdaten...');
    this.stammdatenService.getArtikelStammdaten().subscribe(
      (response: any) => {
        console.log('Artikel stammdaten loaded:', response);
        
        if (response) {
          const stammdaten = response;
          
          // Load artikel art (simple string)
          this.artikelArten = stammdaten.artikel_art ? [stammdaten.artikel_art] : [];
          
          // Load basiseinheiten
          this.basiseinheiten = stammdaten.artikel_basiseinheiten || [];
          
          // Load ausbringungen
          this.ausbringungen = stammdaten.artikel_ausbringungen || [];
          
          // Load status options
          this.statusOptions = stammdaten.artikel_status || [];
          
          // Load produktarten
          this.produktarten = stammdaten.artikel_produktarten || [];
        }
        
        this.loading = false;
        this.patchArtikelForm();
      },
      (error) => {
        console.error('Error loading artikel stammdaten:', error);
        this.loading = false;
        
        // Set empty arrays for dropdowns if loading fails
        this.artikelArten = [];
        this.basiseinheiten = [];
        this.ausbringungen = [];
        this.statusOptions = [];
        this.produktarten = [];
        
        // Still fill the form even if dropdown loading fails
        this.patchArtikelForm();
      }
    );
  }

  /**
   * Dialog functions
   */
  onConfirm(): void {
    if (this.frmArtikel.valid && !this.isSubmitting) {
      this.isSubmitting = true;
      const formData = this.frmArtikel.value;
      
      // Ensure status_id defaults to 1 (active) if not provided
      if (!formData.status_id) {
        formData.status_id = 1;
      }
      
      if (this.data.mode === 'create') {
        this.stammdatenService.createArtikel(formData).subscribe({
          next: (response) => {
            this.snackBar.open('Produkt erfolgreich erstellt', 'Schließen', { duration: 3000 });
            this.dialogRef.close(response);
          },
          error: (error) => {
            console.error('Error creating artikel:', error);
            this.snackBar.open('Fehler beim Erstellen des Produkts', 'Schließen', { duration: 3000 });
            this.isSubmitting = false;
          }
        });
      } else {
        // Edit mode
        this.stammdatenService.updateArtikel(formData.id, formData).subscribe({
          next: (response) => {
            this.snackBar.open('Produkt erfolgreich aktualisiert', 'Schließen', { duration: 3000 });
            this.dialogRef.close(response);
          },
          error: (error) => {
            console.error('Error updating artikel:', error);
            this.snackBar.open('Fehler beim Aktualisieren des Produkts', 'Schließen', { duration: 3000 });
            this.isSubmitting = false;
          }
        });
      }
    }
  }

  onDismiss(): void {
    // Close the dialog, return false
    this.dialogRef.close(false);
  }

  onNoClick(): void {
    this.dialogRef.close();
  }
} 